import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { IconWrapper } from '@flexo/general';
import { Button } from '@flexo/atoms';
import { useDetailsListData, DetailsListType, DetailsListConfig } from '@flexo/hooks';
// import Accordion from '@mui/material/Accordion';
// import AccordionSummary from '@mui/material/AccordionSummary';
// import AccordionDetails from '@mui/material/AccordionDetails';


import styles from './details-list.module.scss';

export interface DetailsListProps {
  type: DetailsListType;
  data?: any;
}

export function DetailsList({ type, data = null }: DetailsListProps) {

  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const {
    isGroup,
    expanded,
    setExpanded,
    singleConfig,
    groupConfigs,
    totalItems,
    hasExpandCollapse,
  } = useDetailsListData(type, data);

  // Helpers
  const emailRegex = /^(?:[a-zA-Z0-9_'^&+\-])+(?:\.(?:[a-zA-Z0-9_'^&+\-])+)*@(?:(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}|\[(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\])$/;

  function isValidEmail(value: unknown): boolean {
    return typeof value === 'string' && emailRegex.test(value);
  }

  function copyToClipboard(text: string) {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => void 0);
      return;
    }
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    } catch {
      // no-op
    }
  }

  // UI rendering function for individual details list items
  function renderDetailsList(config: DetailsListConfig) {
    const { title, isClickable = false, subtitle, dataList, onTitleClick, isDocked = false } = config;

    const {
      Details__Wrapper__Compact,
      Details__Paper,
      Details__Title,
      Details__Title__Clickable,
      Details__Subtitle,
      Details__Content,
      Details__Content__Name,
    } = styles;

    const handleTitleClick = () => {
      if (isClickable && onTitleClick) {
        onTitleClick();
      }
    };

    return (
      <div
        key={uuidv4()}
        className={Details__Wrapper__Compact}
      >
        <div className={Details__Paper}>
          {
            isDocked === false && (
              <>
                {
            isClickable ? (
            <p
              onClick={handleTitleClick}
              className={`heading4 ${Details__Title} ${Details__Title__Clickable}`}
            >
              {(title)}
              <IconWrapper iconName="chevron-right" />
            </p>
          ) : (
            <p className={Details__Title}>{title}</p>
            )
          }
              </>
            )
          }

          {subtitle && (
            <p className={Details__Subtitle}>{(subtitle)}</p>
          )}

          <div>
            {dataList.map((item, index) => {
              const displayValue = item.value || '-';
              const email = isValidEmail(displayValue);
              return (
                <div key={`${item.title}_${index}_${uuidv4()}`} className="flex flex-column">
                  <span
                    className={Details__Content}
                    onClick={email ? () => copyToClipboard(displayValue) : undefined}
                    style={email ? { cursor: 'pointer' } : undefined}
                  >
                    {(displayValue)}
                  </span>
                <span 
                  className={Details__Content__Name}
                  style={{ marginBottom: index === dataList.length - 1 ? 0 : undefined }}
                >
                  {item.title}
                </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Render single item
  function renderSingle(isDocked: boolean = false) {
    if (!singleConfig) return null;
    return renderDetailsList({
      ...singleConfig,
      isDocked: isDocked
    });
  }

  // Render group of items
  function renderGroup( isDocked: boolean = false) {
    const {
      Sites__List__Wrapper,
      Sites__List__Wrapper__Flat,
      Sites__List__Wrapper__Expanded,
      Sites__List__Expand__Button__Wrapper,
      Sites__List__Expand__Button,
      Tall,
    } = styles;

    return (
      <div
        key={`${type}_list_${uuidv4()}`}
        className={`${ Sites__List__Wrapper } ${expanded && Sites__List__Wrapper__Expanded}`}
      >
        {groupConfigs.map((config, index) => {
          // For memberSites, only show first 4 items unless expanded
          if (type === 'memberSites' && !expanded && index >= 4) {
            return null;
          }

          const isInactive = config.isInactive;

          return (
            <div
              key={`item_${index}_${uuidv4()}`}
              style={{ opacity: isInactive ? 0.5 : 1 }}
            >
              {renderDetailsList(config)}
            </div>
          );
        })}

        {hasExpandCollapse && (
          <div className={Sites__List__Expand__Button__Wrapper}>
            <Button
              variant="primary"
              color="secondary"
              size="small"
              label={expanded
                ? t('widgets.memberSitesDetails.collapse')
                : t('widgets.memberSitesDetails.expand', {
                    sitesNumber: totalItems,
                  })}
              onClick={() => setExpanded(!expanded)}
            />
          </div>
        )}
      </div>
    );
  }


  return isGroup ? renderGroup() : renderSingle();

  
  // if ( window.innerWidth < 991 ) {
  //   return <Accordion
  //       className={styles.Accordion}
  //       onChange={(_e, expanded) => setIsExpanded(expanded)}
  //     >
  //       <AccordionSummary
  //         aria-controls="panel1-content"
  //         id="panel1-header"
  //         className={styles.Accordion__Summary}

  //       >
  //         <div className={styles.Accordion__Summary__Title__Wrapper}>
  //           <p>
  //             {(() => {
  //               switch ( type ) {
  //                 case 'memberSites':
  //                   return t('widgets.memberSitesDetails.siteDetails');
  //                 case 'memberDetails':
  //                   return t('widgets.memberDetailsParameters.title');
  //                 case 'siteDetails':
  //                   return t('widgets.siteDetailsParameters.title');
  //                 case 'communityDetails':
  //                   return t('widgets.communityDetailsParameters.title');
  //                 case 'siteMembers':
  //                   return t('widgets.memberDetailsParameters.title');
  //                 default:
  //                   return '';
  //               }
  //             } )()}      
  //               <IconWrapper
  //                 className={`${styles.Accordion__Summary__Icon} ${isExpanded ? 'rotate-90' : 'rotato-0'}`}
  //                 iconName={'chevron-right'}
  //               />
  //           </p>
  //           {(() => {
  //           if (isExpanded) { return ''; }
            
            
  //               switch ( type ) {
  //                 case 'memberSites':
  //                   case 'siteMembers':
  //                   return <p> {groupConfigs[0]?.title}</p>;
  //                 case 'memberDetails':
  //                 case 'siteDetails':
  //                   return <p>{ singleConfig?.subtitle}</p>;
  //                 case 'communityDetails':
  //                   return <p>{ singleConfig?.dataList?.[3]?.value || ''}</p>;
  //                 default:
  //                   return '';
  //               }
  //             } )()} 
  //         </div>
          
  //       </AccordionSummary>
  //       <AccordionDetails>
  //         {isGroup ? renderGroup(true) : renderSingle(true)}
  //       </AccordionDetails>
  //     </Accordion>
  // } else {
  //   return isGroup ? renderGroup() : renderSingle();
  // }

}

export default DetailsList; 